import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useAuth } from "../../App";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { organizationsAPI } from "../../services/api";

const StudentModal = ({
  isOpen,
  onClose,
  onSubmit,
  student,
  trainers = [],
  title,
  loading = false,
}) => {
  const { colors } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    organizationId: "",
    mainTrainerId: "",
    studentStatus: "active",
    bloodType: "",
    tcNo: "",
    gender: "",
    weight: "",
    age: "",
    height: "",
    birthDate: "",
    profilePicture: "",
    color: "",
    notes: "",
    startDate: "",
  });
  const [organizations, setOrganizations] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("organization");

  const { user } = useAuth();
  const { selectedOrganizationId } = useApp();
  const [userRole, setUserRole] = useState("");

  const getUserRole = () => {
    try {
      if (user?.session) {
        const idToken = user.session.getIdToken();
        const payload = idToken.payload;
        const groups = payload["cognito:groups"] || [];

        if (groups.includes("SuperAdmins")) return "super_admin";
        if (groups.includes("OrganizationManager"))
          return "organization_manager";
        if (groups.includes("Trainers")) return "trainer";
        if (groups.includes("Students")) return "student";
      }
      return "student";
    } catch (error) {
      console.error("Error getting user role:", error);
      return "student";
    }
  };

  useEffect(() => {
    setUserRole(getUserRole());
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAll();
      const orgs = response.data.data || [];

      // Filter out 'all' option if it exists
      // If a specific organization is selected in context, only show that one
      if (selectedOrganizationId && selectedOrganizationId !== "all") {
        setOrganizations(
          orgs.filter((org) => org.id === selectedOrganizationId),
        );
      } else {
        setOrganizations(orgs.filter((org) => org.id !== "all"));
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
      setOrganizations([]);
    }
  };

  useEffect(() => {
    if (student) {
      setFormData({
        name: student.name || "",
        surname: student.surname || "",
        email: student.email || "",
        phone: student.phone || "",
        organizationId: student.organizationId || "",
        mainTrainerId: student.mainTrainerId || "",
        studentStatus: student.studentStatus || "active",
        bloodType: student.bloodType || "",
        tcNo: student.tcNo || "",
        gender: student.gender || "",
        weight: student.weight ? student.weight.toString() : "",
        age: student.age ? student.age.toString() : "",
        height: student.height ? student.height.toString() : "",
        birthDate: student.birthDate || "",
        profilePicture: student.profilePicture || "",
        color: student.color || "",
        notes: student.notes || "",
        startDate: student.startDate || "",
      });
    } else {
      setFormData({
        name: "",
        surname: "",
        email: "",
        phone: "",
        email: "",
        phone: "",
        organizationId:
          selectedOrganizationId && selectedOrganizationId !== "all"
            ? selectedOrganizationId
            : "",
        mainTrainerId: "",
        studentStatus: "active",
        bloodType: "",
        tcNo: "",
        gender: "",
        weight: "",
        age: "",
        height: "",
        birthDate: "",
        profilePicture: "",
        color: "",
        notes: "",
        startDate: new Date().toISOString().split("T")[0],
      });
    }
    setErrors({});
    // If editing, start at basic info, if creating start at organization
    setActiveTab(student ? "basic" : "organization");
  }, [student, isOpen]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === "organization") {
      if (!formData.organizationId) {
        newErrors.organizationId = "Organizasyon seçimi zorunludur";
      }
    }

    if (step === "basic") {
      if (!formData.name.trim()) newErrors.name = "İsim gereklidir";
      if (!formData.surname.trim()) newErrors.surname = "Soyisim gereklidir";
      if (!formData.email.trim()) {
        newErrors.email = "E-posta gereklidir";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Geçerli bir e-posta adresi girin";
      }
      if (!formData.phone.trim()) newErrors.phone = "Telefon gereklidir";
    }

    if (step === "details") {
      if (!formData.birthDate) newErrors.birthDate = "Doğum tarihi gereklidir";
      if (!formData.gender) newErrors.gender = "Cinsiyet gereklidir";
      if (!formData.age) newErrors.age = "Yaş gereklidir";
      if (!formData.weight) newErrors.weight = "Kilo gereklidir";
      if (!formData.height) newErrors.height = "Boy gereklidir";

      if (
        formData.age &&
        (isNaN(formData.age) || formData.age < 0 || formData.age > 120)
      ) {
        newErrors.age = "Yaş 0 ile 120 arasında olmalıdır";
      }
      if (formData.weight && (isNaN(formData.weight) || formData.weight <= 0)) {
        newErrors.weight = "Kilo pozitif bir sayı olmalıdır";
      }
      if (formData.height && (isNaN(formData.height) || formData.height <= 0)) {
        newErrors.height = "Boy pozitif bir sayı olmalıdır";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    // Block the click's default action. Without this, React swaps this
    // button into the type="submit" save button mid-click (same DOM node),
    // and the browser then runs the click's default action against the NEW
    // type — submitting the form in the same click that changed the step.
    e.preventDefault();
    if (validateStep(activeTab)) {
      if (activeTab === "organization") setActiveTab("basic");
      else if (activeTab === "basic") setActiveTab("details");
    }
  };

  const handleBack = () => {
    if (activeTab === "details") setActiveTab("basic");
    else if (activeTab === "basic") setActiveTab("organization");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all steps before submitting
    let allValid = true;
    for (const tab of tabs) {
      if (!validateStep(tab.id)) {
        allValid = false;
        setActiveTab(tab.id);
        break;
      }
    }
    if (!allValid) return;

    try {
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        mainTrainerId: formData.mainTrainerId || null,
      };

      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "") {
          submitData[key] = null;
        }
      });

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: "organization", name: "Organizasyon" },
    { id: "basic", name: "Temel Bilgiler" },
    { id: "details", name: "Kişisel Detaylar" },
  ];

  return (
    <div className="modal-overlay">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={`
        relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col
        ${colors.cardBg} ${colors.cardBorder}
        rounded-xl shadow-2xl border
        transform transition-all duration-200 ease-out scale-100
      `}
      >
        {/* Header */}
        <div
          className={`
          flex items-center justify-between p-6 border-b shrink-0
          ${colors.cardBorder}
        `}
        >
          <h2 className={`text-xl font-bold ${colors.textPrimary}`}>{title}</h2>
          <button
            onClick={onClose}
            className={`
              p-2 rounded-lg transition-colors
              ${colors.textSecondary}
              hover:bg-gray-100 dark:hover:bg-zinc-800
              hover:text-gray-900 dark:hover:text-white
            `}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className={`px-4 md:px-6 py-4 border-b ${colors.border}`}>
          <div className="flex items-center justify-between">
            {tabs.map((tab, index) => {
              const isActive = activeTab === tab.id;
              const isCompleted = tabs.findIndex((t) => t.id === activeTab) > index;
              return (
                <React.Fragment key={tab.id}>
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                    <div
                      className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs md:text-sm font-bold transition-all duration-300 ${isActive
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 ring-4 ring-blue-500/20'
                        : isCompleted
                          ? 'bg-green-500 text-white'
                          : `bg-gray-200 dark:bg-gray-700 ${colors.textMuted}`
                        }`}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    <span className={`text-[10px] md:text-xs font-medium text-center leading-tight max-w-[72px] md:max-w-none ${isActive ? 'text-blue-600 dark:text-blue-400' : isCompleted ? 'text-green-600 dark:text-green-400' : colors.textMuted
                      }`}>
                      {tab.name}
                    </span>
                  </div>
                  {index < tabs.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1.5 md:mx-3 rounded-full mb-5 ${isCompleted ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="modal-body">
            {/* Organization Tab */}
            {activeTab === "organization" && (
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="organizationId"
                    className="form-label"
                  >
                    Organizasyon *
                  </label>
                  <select
                    id="organizationId"
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                      } ${colors.inputText} focus:outline-none focus:ring-2 ${colors.inputFocus
                      } focus:border-transparent ${errors.organizationId
                        ? "border-red-300"
                        : colors.inputBorder
                      }`}
                  >
                    <option value="">Organizasyon seçin</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {errors.organizationId && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.organizationId}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-4">
                <div className="modal-grid">
                  <div>
                    <label
                      htmlFor="name"
                      className="form-label"
                    >
                      İsim *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} ${colors.inputPlaceholder
                        } focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.name ? "border-red-300" : colors.inputBorder
                        }`}
                      placeholder="İsim girin"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="surname"
                      className="form-label"
                    >
                      Soyisim *
                    </label>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      value={formData.surname}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} ${colors.inputPlaceholder
                        } focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.surname ? "border-red-300" : colors.inputBorder
                        }`}
                      placeholder="Soyisim girin"
                    />
                    {errors.surname && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.surname}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="form-label"
                  >
                    E-posta *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                      } ${colors.inputText} ${colors.inputPlaceholder
                      } focus:outline-none focus:ring-2 ${colors.inputFocus
                      } focus:border-transparent ${errors.email ? "border-red-300" : colors.inputBorder
                      }`}
                    placeholder="E-posta adresi girin"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="form-label"
                  >
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                      } ${colors.inputText} ${colors.inputPlaceholder
                      } focus:outline-none focus:ring-2 ${colors.inputFocus
                      } focus:border-transparent ${errors.phone ? "border-red-300" : colors.inputBorder
                      }`}
                    placeholder="Telefon numarası girin"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                <div className="modal-grid">
                  <div>
                    <label
                      htmlFor="mainTrainerId"
                      className="form-label"
                    >
                      Ana Eğitmen
                    </label>
                    <select
                      id="mainTrainerId"
                      name="mainTrainerId"
                      value={formData.mainTrainerId}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Eğitmen seçin</option>
                      {trainers.map((trainer) => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="studentStatus"
                      className="form-label"
                    >
                      Durum
                    </label>
                    <select
                      id="studentStatus"
                      name="studentStatus"
                      value={formData.studentStatus}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="active">Aktif</option>
                      <option value="inactive">Pasif</option>
                      <option value="suspended">Askıda</option>
                      <option value="graduated">Mezun</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="startDate"
                    className="form-label"
                  >
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
            )}

            {/* Personal Details Tab */}
            {activeTab === "details" && (
              <div className="space-y-4">
                <div className="modal-grid">
                  {1 == 1 ? (
                    <div>
                      <label
                        htmlFor="gender"
                        className="form-label"
                      >
                        Cinsiyet
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                          } ${colors.inputText} focus:outline-none focus:ring-2 ${colors.inputFocus
                          } focus:border-transparent ${errors.gender ? "border-red-300" : colors.inputBorder
                          }`}
                      >
                        <option value="">Cinsiyet seçin</option>
                        <option value="Male">Erkek</option>
                        <option value="Female">Kadın</option>
                      </select>
                      {errors.gender && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.gender}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div></div>
                  )}

                  <div>
                    <label
                      htmlFor="age"
                      className="form-label"
                    >
                      Yaş
                    </label>
                    <input
                      type="number"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      min="0"
                      max="120"
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} ${colors.inputPlaceholder
                        } focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.age ? "border-red-300" : colors.inputBorder
                        }`}
                      placeholder="25"
                    />
                    {errors.age && (
                      <p className="mt-1 text-sm text-red-600">{errors.age}</p>
                    )}
                  </div>
                </div>

                <div className="modal-grid">
                  <div>
                    <label
                      htmlFor="weight"
                      className="form-label"
                    >
                      Kilo (kg)
                    </label>
                    <input
                      type="number"
                      id="weight"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} ${colors.inputPlaceholder
                        } focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.weight ? "border-red-300" : colors.inputBorder
                        }`}
                      placeholder="70"
                    />
                    {errors.weight && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.weight}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="height"
                      className="form-label"
                    >
                      Boy (cm)
                    </label>
                    <input
                      type="number"
                      id="height"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      min="0"
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} ${colors.inputPlaceholder
                        } focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.height ? "border-red-300" : colors.inputBorder
                        }`}
                      placeholder="172"
                    />
                    {errors.height && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.height}
                      </p>
                    )}
                  </div>
                </div>

                <div className="modal-grid">
                  <div>
                    <label
                      htmlFor="birthDate"
                      className="form-label"
                    >
                      Doğum Tarihi
                    </label>
                    <input
                      type="date"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg
                        } ${colors.inputText} focus:outline-none focus:ring-2 ${colors.inputFocus
                        } focus:border-transparent ${errors.birthDate ? "border-red-300" : colors.inputBorder
                        }`}
                    />
                    {errors.birthDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.birthDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="bloodType"
                      className="form-label"
                    >
                      Kan Grubu
                    </label>
                    <select
                      id="bloodType"
                      name="bloodType"
                      value={formData.bloodType}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Kan grubu seçin</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>

                <div className="modal-grid">
                  <div>
                    <label
                      htmlFor="tcNo"
                      className="form-label"
                    >
                      TC Kimlik No
                    </label>
                    <input
                      type="text"
                      id="tcNo"
                      name="tcNo"
                      value={formData.tcNo}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="11066507556"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="color"
                      className="form-label"
                    >
                      Tema Rengi
                    </label>
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className={`w-full h-10 border ${colors.border} rounded-lg cursor-pointer`}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="notes"
                    className="form-label"
                  >
                    Notlar
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows="4"
                    value={formData.notes}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Öğrenci hakkında ek notlar..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Buttons - Fixed Footer */}
          <div
            className="modal-footer"
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              İptal
            </button>

            <div className="flex space-x-3">
              {activeTab !== "organization" && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-secondary"
                >
                  Geri
                </button>
              )}

              {activeTab !== "details" ? (
                /* key keeps this DOM node separate from the submit button —
                   reusing one node lets a single click both advance the step
                   and fire the form's submit default action */
                <button
                  key="next-step"
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                >
                  İleri
                </button>
              ) : (
                <button
                  key="submit-form"
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading
                    ? "Kaydediliyor..."
                    : student
                      ? "Öğrenciyi Güncelle"
                      : "Öğrenci Ekle"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentModal;
